<template>
  <div ref="root">
    <KeyCap
      ref="keycap"
      :row="row"
      :col="col"
      :x="x"
      :y="y"
      :rot="rot"
      :height="height"
      :width="width"
      :selected="isOpen"
      :highlighted="hl.isHighlightedKey(row, col)"
      @click="toggle"
      draggable="true"
      @dragstart="(e) => emitDrag('dragstart', e)"
      @dragenter="(e) => emitDrag('dragenter', e)"
      @dragleave="(e) => emitDrag('dragleave', e)"
      @dragover.prevent="(e) => emitDrag('dragover', e)"
      @dragend="(e) => emitDrag('dragend', e)"
      @drop.prevent="(e) => emitDrag('drop', e)"
      class="dragover:saturate-[.3]"
      :class="helpIsSelected() ? 'tour-key-cap' : ''"
    />
    <PopUp
      ref="popup"
      v-if="!hidden || (helpIsSelected() && help.keyMenuExpand)"
      :show="true"
      :reference="keycap?.$el"
      :placement="col < kb.columns / 2 ? 'right' : 'left'"
      :offset="-7"
    >
      <ActionMenu
        :class="helpIsSelected() ? 'tour-key-menu' : ''"
        :action="action"
        @newAction="onNewAction"
        :cacheKey="`${row}-${col}`"
      />
    </PopUp>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { ActionInternal } from "@/keyboard";
import { useKeyboardStore, useHelpStore, useHighlightStore } from "@/stores";
import { useOnClickOutside } from "@/composables";
import PopUp from "@/components/utils/PopUp.vue";
import ActionMenu from "@/components/actions/ActionMenu.vue";
import KeyCap from "@/components/keys/KeyCap.vue";

const props = defineProps<{
  // Props from KeyCap
  x: number;
  y: number;
  rot: number;
  height: number;
  width: number;
  row: number;
  col: number;
}>();

type KeyCapT = InstanceType<typeof KeyCap>;
const emit = defineEmits<{
  (_e: "opened"): void;
  (_e: "closed"): void;
  (_e: "dragstart", ev: DragEvent, kc: KeyCapT): void;
  (_e: "dragenter", ev: DragEvent, kc: KeyCapT): void;
  (_e: "dragleave", ev: DragEvent, kc: KeyCapT): void;
  (_e: "dragover", ev: DragEvent, kc: KeyCapT): void;
  (_e: "dragend", ev: DragEvent, kc: KeyCapT): void;
  (_e: "drop", ev: DragEvent, kc: KeyCapT): void;
}>();

const kb = useKeyboardStore();
const help = useHelpStore();
const hl = useHighlightStore();

const keycap = ref<InstanceType<typeof KeyCap> | null>(null);
const popup = ref<InstanceType<typeof PopUp> | null>(null);
const root = ref<HTMLElement | null>(null);

const hidden = ref(true);
const helpIsSelected = () => help.isKeySelected(props.row, props.col);
const action = computed(
  (): ActionInternal =>
    helpIsSelected() && help.overrideKeyAction != null
      ? help.overrideKeyAction
      : kb.getAction(props.row, props.col)
);

type DragEventName =
  | "dragstart"
  | "dragenter"
  | "dragleave"
  | "dragover"
  | "dragend"
  | "drop";
const emitDrag = (name: DragEventName, e: DragEvent) => {
  if (keycap.value != null) {
    emit(name, e, keycap.value);
  }
};

// Open/close logic
const isOpen = computed(() => !hidden.value);
const close = () => {
  hidden.value = true;
  emit("closed");
};
const open = () => {
  hidden.value = false;
  // popper.update();
  emit("opened");
};
const toggle = () => (isOpen.value ? close() : open());

defineExpose({ isOpen, close, open, toggle, keycap });

// Close popup on "click outside"
useOnClickOutside(root, close);

// Adjust popper when action (and so content) changes
const onNewAction = (act: ActionInternal) => {
  kb.setAction(props.row, props.col, act);
};
</script>
